/**
 * In-headset debug HUD: a small camera-attached canvas plane with frame time, triangles, draw
 * calls, peers and members. Redrawn twice a second, never per frame.
 */
import * as THREE from 'three';

export interface HudStats {
  fps: number;
  frameMs: number;
  triangles: number;
  drawCalls: number;
  members: number;
  peers: number;
  scene: string;
  extra?: string;
}

export class DebugHud {
  readonly mesh: THREE.Mesh;
  /** A hand icon beside the HUD slot, shown while the local hand is raised (HUD on or off). */
  readonly hand: THREE.Mesh;
  private readonly handCanvas = document.createElement('canvas');
  private readonly handTexture: THREE.CanvasTexture;
  private readonly canvas = document.createElement('canvas');
  private readonly texture: THREE.CanvasTexture;
  private lastDraw = 0;

  constructor(camera: THREE.Camera) {
    this.canvas.width = 512;
    this.canvas.height = 192;
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(0.24, 0.09),
      new THREE.MeshBasicMaterial({
        map: this.texture,
        transparent: true,
        depthTest: false,
        toneMapped: false,
      }),
    );
    this.mesh.name = 'debug-hud';
    this.mesh.position.set(-0.2, -0.17, -0.55);
    this.mesh.rotation.set(-0.15, 0.25, 0);
    this.mesh.renderOrder = 9000;
    this.mesh.visible = false;
    camera.add(this.mesh);
    this.handCanvas.width = 160;
    this.handCanvas.height = 160;
    this.handTexture = new THREE.CanvasTexture(this.handCanvas);
    this.handTexture.colorSpace = THREE.SRGBColorSpace;
    const hctx = this.handCanvas.getContext('2d')!;
    hctx.fillStyle = 'rgba(11, 15, 21, 0.85)';
    hctx.beginPath();
    hctx.arc(80, 80, 76, 0, Math.PI * 2);
    hctx.fill();
    hctx.font = '96px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif';
    hctx.textAlign = 'center';
    hctx.textBaseline = 'middle';
    hctx.fillStyle = '#ffd37a';
    hctx.fillText('✋', 80, 88);
    this.handTexture.needsUpdate = true;
    this.hand = new THREE.Mesh(
      new THREE.PlaneGeometry(0.05, 0.05),
      new THREE.MeshBasicMaterial({
        map: this.handTexture,
        transparent: true,
        depthTest: false,
        toneMapped: false,
      }),
    );
    this.hand.name = 'hand-raised';
    this.hand.position.set(0.22, -0.17, -0.55);
    this.hand.rotation.set(-0.15, -0.25, 0);
    this.hand.renderOrder = 9000;
    this.hand.visible = false;
    camera.add(this.hand);
  }

  setHandRaised(on: boolean): void {
    this.hand.visible = on;
  }

  get visible(): boolean {
    return this.mesh.visible;
  }

  setVisible(visible: boolean): void {
    this.mesh.visible = visible;
  }

  update(stats: HudStats, now = performance.now()): void {
    if (!this.mesh.visible || now - this.lastDraw < 500) return;
    this.lastDraw = now;
    const ctx = this.canvas.getContext('2d')!;
    ctx.clearRect(0, 0, 512, 192);
    ctx.fillStyle = 'rgba(11, 15, 21, 0.82)';
    ctx.fillRect(0, 0, 512, 192);
    ctx.fillStyle = stats.fps >= 70 ? '#4cd97b' : stats.fps >= 58 ? '#f2b44b' : '#ff6b7a';
    ctx.font = '700 54px "Inter", system-ui, sans-serif';
    ctx.textBaseline = 'top';
    ctx.fillText(`${stats.fps.toFixed(0)} fps`, 18, 14);
    ctx.fillStyle = '#f2f6fb';
    ctx.font = '500 30px "Inter", system-ui, sans-serif';
    ctx.fillText(`${stats.frameMs.toFixed(1)} ms`, 300, 26);
    ctx.font = '500 26px "Inter", system-ui, sans-serif';
    ctx.fillStyle = '#a9b6c6';
    ctx.fillText(
      `${(stats.triangles / 1000).toFixed(0)}k tris  ${stats.drawCalls} calls (stereo)`,
      18,
      84,
    );
    ctx.fillText(`${stats.members} in room  ${stats.peers} voice peers`, 18, 118);
    ctx.fillText(stats.extra ?? stats.scene, 18, 152);
    this.texture.needsUpdate = true;
  }
}
